/**
 * Modal form for creating custom review items
 * Phase 4 of Human-AI Collaborative Reviews implementation
 */

import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Plus, X, FileText, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface DocumentReference {
  text: string;
  page_number?: number;
  document_id?: string;
  document_metadata_id?: string;
}

interface AddReviewItemModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  reviewType: 'contract' | 'quantity' | 'calculation';
  claimId: string;
  onSubmit: (data: {
    title: string;
    judgment: string;
    explanation: string;
    claim_passage?: DocumentReference;
    contract_passages?: DocumentReference[];
  }) => void;
  isLoading?: boolean;
}

const JUDGMENT_OPTIONS = {
  contract: [
    { value: 'valid', label: 'Valid', color: 'bg-green-100 text-green-800 border-green-200' },
    { value: 'invalid', label: 'Invalid', color: 'bg-red-100 text-red-800 border-red-200' },
    { value: 'unclear', label: 'Unclear', color: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
  ],
  quantity: [
    { value: 'valid', label: 'Valid', color: 'bg-green-100 text-green-800 border-green-200' },
    { value: 'excessive', label: 'Excessive', color: 'bg-red-100 text-red-800 border-red-200' },
    { value: 'unclear', label: 'Unclear', color: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
  ],
  calculation: [
    { value: 'valid', label: 'Valid', color: 'bg-green-100 text-green-800 border-green-200' },
    { value: 'invalid', label: 'Invalid', color: 'bg-red-100 text-red-800 border-red-200' },
    { value: 'unclear', label: 'Unclear', color: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
  ],
};

export function AddReviewItemModal({
  isOpen,
  onOpenChange,
  reviewType,
  claimId: _claimId,
  onSubmit,
  isLoading = false,
}: AddReviewItemModalProps) {
  const [formData, setFormData] = useState({
    title: '',
    judgment: '',
    explanation: '',
    claimPassage: '',
    claimPageNumber: '1',
    contractPassages: [] as { text: string; pageNumber: string }[],
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const judgmentOptions = JUDGMENT_OPTIONS[reviewType];
  const selectedJudgment = judgmentOptions.find(opt => opt.value === formData.judgment);

  const resetForm = () => {
    setFormData({
      title: '',
      judgment: '',
      explanation: '',
      claimPassage: '',
      claimPageNumber: '1',
      contractPassages: [],
    });
    setErrors({});
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.judgment) {
      newErrors.judgment = 'Judgment is required';
    }

    if (!formData.explanation.trim()) {
      newErrors.explanation = 'Explanation is required';
    }

    if (formData.explanation.trim().length < 10) {
      newErrors.explanation = 'Explanation must be at least 10 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Prepare claim passage
    const claimPassage = formData.claimPassage.trim() ? {
      text: formData.claimPassage.trim(),
      page_number: parseInt(formData.claimPageNumber) || 1,
      document_id: '',
    } : undefined;

    // Prepare contract passages
    const contractPassages = formData.contractPassages
      .filter(passage => passage.text.trim())
      .map(passage => ({
        text: passage.text.trim(),
        page_number: parseInt(passage.pageNumber) || 1,
        document_metadata_id: '',
      }));

    onSubmit({
      title: formData.title.trim(),
      judgment: formData.judgment,
      explanation: formData.explanation.trim(),
      claim_passage: claimPassage,
      contract_passages: contractPassages.length > 0 ? contractPassages : undefined,
    });

    resetForm();
  };

  const handleCancel = () => {
    resetForm();
    onOpenChange(false);
  };

  const addContractPassage = () => {
    setFormData(prev => ({
      ...prev,
      contractPassages: [...prev.contractPassages, { text: '', pageNumber: '1' }],
    }));
  };

  const removeContractPassage = (index: number) => {
    setFormData(prev => ({
      ...prev,
      contractPassages: prev.contractPassages.filter((_, i) => i !== index),
    }));
  };

  const updateContractPassage = (index: number, field: 'text' | 'pageNumber', value: string) => {
    setFormData(prev => ({
      ...prev,
      contractPassages: prev.contractPassages.map((passage, i) => 
        i === index ? { ...passage, [field]: value } : passage
      ),
    }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" />
            Add Custom Review Item
          </DialogTitle>
          <DialogDescription>
            Create a custom review item for {reviewType} review. All required fields must be completed.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Brief description of the review item"
              className={cn(errors.title && "border-red-500")}
              disabled={isLoading}
            />
            {errors.title && (
              <p className="text-sm text-red-600">{errors.title}</p>
            )}
          </div>

          {/* Judgment */}
          <div className="space-y-2">
            <Label htmlFor="judgment">Judgment *</Label>
            <Select 
              value={formData.judgment} 
              onValueChange={(value) => setFormData(prev => ({ ...prev, judgment: value }))}
              disabled={isLoading}
            >
              <SelectTrigger className={cn(errors.judgment && "border-red-500")}>
                <SelectValue placeholder="Select judgment" />
              </SelectTrigger>
              <SelectContent>
                {judgmentOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    <div className="flex items-center gap-2">
                      <div className={cn("w-3 h-3 rounded-full", option.color)} />
                      <span>{option.label}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedJudgment && (
              <Badge variant="outline" className={cn("mt-2", selectedJudgment.color)}>
                {selectedJudgment.label}
              </Badge>
            )}
            {errors.judgment && (
              <p className="text-sm text-red-600">{errors.judgment}</p>
            )}
          </div>

          {/* Explanation */}
          <div className="space-y-2">
            <Label htmlFor="explanation">Analysis/Explanation *</Label>
            <Textarea
              id="explanation"
              value={formData.explanation}
              onChange={(e) => setFormData(prev => ({ ...prev, explanation: e.target.value }))}
              placeholder="Detailed analysis and reasoning for this review item"
              className={cn("min-h-[100px]", errors.explanation && "border-red-500")}
              disabled={isLoading}
            />
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>{formData.explanation.length} characters</span>
              <span>Minimum 10 characters required</span>
            </div>
            {errors.explanation && (
              <p className="text-sm text-red-600">{errors.explanation}</p>
            )}
          </div>

          {/* Claim Document Reference */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-blue-600" />
              <Label className="text-sm font-medium">Claim Document Reference (Optional)</Label>
            </div>
            
            <div className="grid grid-cols-4 gap-2">
              <div className="col-span-3">
                <Textarea
                  value={formData.claimPassage}
                  onChange={(e) => setFormData(prev => ({ ...prev, claimPassage: e.target.value }))}
                  placeholder="Relevant text from claim document"
                  className="min-h-[80px]"
                  disabled={isLoading}
                />
              </div>
              <div>
                <Label htmlFor="claimPage" className="text-xs">Page</Label>
                <Input
                  id="claimPage"
                  type="number"
                  min="1"
                  value={formData.claimPageNumber}
                  onChange={(e) => setFormData(prev => ({ ...prev, claimPageNumber: e.target.value }))}
                  disabled={isLoading}
                />
              </div>
            </div>
          </div>

          {/* Contract Document References */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-green-600" />
                <Label className="text-sm font-medium">Contract Document References (Optional)</Label>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addContractPassage}
                disabled={isLoading}
              >
                <Plus className="h-3 w-3 mr-1" />
                Add Reference
              </Button>
            </div>

            {formData.contractPassages.map((passage, index) => (
              <div key={index} className="grid grid-cols-4 gap-2 p-3 border rounded-lg">
                <div className="col-span-3">
                  <Textarea
                    value={passage.text}
                    onChange={(e) => updateContractPassage(index, 'text', e.target.value)}
                    placeholder="Relevant text from contract document"
                    className="min-h-[80px]"
                    disabled={isLoading}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Page</Label>
                  <Input
                    type="number"
                    min="1"
                    value={passage.pageNumber}
                    onChange={(e) => updateContractPassage(index, 'pageNumber', e.target.value)}
                    disabled={isLoading}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeContractPassage(index)}
                    className="w-full text-red-600 hover:text-red-700"
                    disabled={isLoading}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </form>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Plus className="h-4 w-4 mr-2" />
                Create Item
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}